import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, IconButton, Box, Stepper, Step, StepLabel, Tabs, Tab} from '@mui/material'
import {ArrowBack, CheckCircle} from '@mui/icons-material'
import {caregiverApi, accountApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {useNavigate, Link} from 'react-router-dom'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {parseError, phoneRegex, emailRegex} from '../../utils/helpers'
import {useMobile} from '../../hooks'
import {Backgrounds, ContactAndAddress, Demographics, EmploymentInfo, LanguageAndPreferences, AdditionalNotes} from './components'
import {
    backgroundDocumentSections,
    documentUploadFormData,
    entryToExperience,
    validateDocumentCategories,
} from '../../utils/caregiverBackgrounds'
import {format} from 'date-fns'

export default () => {
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const [activeStep, setActiveStep] = useState(0)
    const [nextLabel, setNextLabel] = useState('Next')
    const navigate = useNavigate()
    const {mobile} = useMobile()
    const [demographicsData, setDemographicsData] = useState(null)
    const [contactAndAddressData, setContactAndAddressData] = useState(null)
    const [employmentInfoData, setEmploymentInfoData] = useState(null)
    const [languageAndPreferencesData, setLanguageAndPreferencesData] = useState(null)
    const [backgroundsData, setBackgroundsData] = useState(null)
    const [additionalNotesData, setAdditionalNotesData] = useState(null)
    const [avatarUrl, setAvatarUrl] = useState(null)
    const [file, setFile] = useState(null)
    const [branches, setBranches] = useState([])
    const [selectedBranches, setSelectedBranches] = useState([])
    const steps = ['Demographics', 'Contact & Address', 'Employee Info', 'Language & Preferences', 'Backgrounds', 'Additional Notes']
    const demographicsForm = useForm({
        resolver: yupResolver(
            yup.object().shape({
                branch_ids: yup.array().of(yup.string()),
                branch_ids_helper: selectedBranches?.length > 0 ? yup.string().notRequired() : yup.string().required('Branches is required'),
                primary_branch_id: yup.string(),
                firstName: yup.string().required('First Name is required'),
                lastName: yup.string().required('Last Name is required'),
                initial: yup.string(),
                email: yup
                    .string()
                    .required('Email is required')
                    .test('is-valid-email', 'Invalid email address', value => emailRegex.test(value)),
                gender: yup.string().required('Gender is required'),
                ssn: yup.string().required('SSN is required'),
                dateOfBirth: yup.date().required('Date of Birth is required'),
                maritalStatus: yup.string(),
                ethnicity: yup.string(),
            }),
        ),
    })
    const contactAndAddressForm = useForm({
        resolver: yupResolver(
            yup.object().shape({
                primaryPhoneNumberType: yup.string().default('mobile'),
                primaryPhoneNumber: yup
                    .string()
                    .required('Phone number is required')
                    .test('is-valid-phone', 'Invalid phone number', value => phoneRegex.test(value)),
                primaryPhoneNumbers: yup.array().of(
                    yup.object().shape({
                        type: yup.string().default('mobile'),
                        number: yup.string().test('phone-optional', 'Invalid phone number', val => {
                            if (!val || val.trim() === '') return true
                            return phoneRegex.test(val)
                        }),
                    }),
                ),
                streetAddressLine1: yup.string().required('Address Line 1 is required'),
                streetAddressLine2: yup.string(),
                city: yup.string().required('City is required'),
                state: yup.string().required('State is required'),
                zipCode: yup
                    .string()
                    .required('Zip Code is required')
                    .min(5, 'Zip Code must be between 5 and 20 characters')
                    .max(20, 'Zip Code must be between 5 and 20 characters')
                    .matches(/^\d+$/, 'Zip Code must contain only numbers'),
            }),
        ),
    })
    const employmentInfoForm = useForm({
        resolver: yupResolver(
            yup.object().shape({
                employmentType: yup.string().required('Employment Type is required'),
                applicationDate: yup.date().required('Application Date is required'),
                referralSource: yup.string(),
                referralPerson: yup.string(),
            }),
        ),
    })
    const languageAndPreferencesForm = useForm({
        resolver: yupResolver(
            yup.object().shape({
                comfortableWithDogs: yup.boolean().default(false),
                comfortableWithCats: yup.boolean().default(false),
                ableToAssistWithPetCare: yup.boolean().default(false),
                man: yup.boolean().default(false),
                woman: yup.boolean().default(false),
                fullTime: yup.boolean().default(false),
                partTime: yup.boolean().default(false),
                liveIn: yup.boolean().default(false),
                hourlyShifts: yup.boolean().default(false),
                languages: yup.array().of(yup.string()),
                comfortableWorkingInaHouseholdWithSmokers: yup.boolean().default(false),
                hasValidDriversLicenseClient: yup.boolean().default(false),
                skilledInWoundCareManagement: yup.boolean().default(false),
                experiencedInPhysicalTherapyAssistance: yup.boolean().default(false),
                experiencedUsingMobilityEquipment: yup.boolean().default(false),
            }),
        ),
    })
    const backgroundsForm = useForm({
        resolver: yupResolver(
            yup.object().shape({
                // internalNote: yup.string(),
            }),
        ),
    })

    const additionalNotesForm = useForm({
        resolver: yupResolver(
            yup.object().shape({
                internalNote: yup.string(),
            }),
        ),
    })

    useEffect(() => {
        accountApi.getBranches().then(
            ({
                data: {
                    data: {branches},
                },
            }) => {
                setBranches(branches)
            },
        )
    }, [])

    useEffect(() => {
        if (activeStep == 5) {
            setNextLabel('Add Caregiver')
        } else {
            setNextLabel('Next')
        }
    }, [activeStep])

    useEffect(() => {
        if (additionalNotesData) {
            submit()
        }
    }, [additionalNotesData])

    const previous = () => {
        setActiveStep(prev => --prev)
    }

    const next = () => {
        if (activeStep == 0) {
            demographicsForm.handleSubmit(data => {
                setDemographicsData(data)
                setActiveStep(prev => ++prev)
            })()
        } else if (activeStep == 1) {
            contactAndAddressForm.handleSubmit(data => {
                setContactAndAddressData(data)
                setActiveStep(prev => ++prev)
            })()
        } else if (activeStep == 2) {
            employmentInfoForm.handleSubmit(data => {
                setEmploymentInfoData(data)
                setActiveStep(prev => ++prev)
            })()
        } else if (activeStep == 3) {
            languageAndPreferencesForm.handleSubmit(data => {
                setLanguageAndPreferencesData(data)
                setActiveStep(prev => ++prev)
            })()
        } else if (activeStep == 4) {
            if (!validateDocumentCategories(backgroundsForm)) return
            backgroundsForm.handleSubmit(data => {
                setBackgroundsData(data)
                setActiveStep(prev => ++prev)
            })()
        } else if (activeStep == 5) {
            additionalNotesForm.handleSubmit(data => {
                setAdditionalNotesData(data)
            })()
        }
    }

    // A branch the new caregiver is assigned to. The photo and Backgrounds
    // endpoints check the assignment, and the branch selected in the app may
    // not be one of theirs.
    const assignedBranchId = () => demographicsData?.primary_branch_id || demographicsData?.branch_ids?.[0] || branchId

    // Resolves to whether the photo made it; never rejects.
    const uploadPhoto = async caregiverId => {
        if (!file) return true
        const formData = new FormData()
        formData.append('file', file)
        try {
            await caregiverApi.uploadCaregiverPhoto(assignedBranchId(), caregiverId, formData)
            return true
        } catch {
            return false
        }
    }

    // Saves the Backgrounds step's work experiences and documents. Resolves to
    // the names of what didn't make it; never rejects.
    const saveBackgrounds = async caregiverId => {
        const missing = []
        const experiences = backgroundsData?.workExperiences ?? []
        if (experiences.length > 0) {
            try {
                await caregiverApi.saveCaregiverWorkExperiences(assignedBranchId(), caregiverId, {
                    work_experiences: experiences.map(entryToExperience),
                })
            } catch {
                missing.push('work experiences')
            }
        }
        for (const {name, documentType} of backgroundDocumentSections) {
            for (const entry of backgroundsData?.[name] ?? []) {
                try {
                    await caregiverApi.uploadCaregiverBackgroundDocument(assignedBranchId(), caregiverId, documentUploadFormData(entry, documentType))
                } catch {
                    missing.push(entry.file.name)
                }
            }
        }
        return missing
    }

    const addedMessage = (photoUploaded, missingBackgrounds) => {
        if (photoUploaded && missingBackgrounds.length === 0) {
            return {
                status: 'success',
                title: 'Caregiver Added Successfully',
                description: 'The caregiver is now part of your team and ready for assignment.',
            }
        }
        if (missingBackgrounds.length === 0) {
            return {
                status: 'warning',
                title: 'Caregiver Added Without Photo',
                description: "The caregiver was added, but the photo couldn't be uploaded. You can add it from their profile.",
            }
        }
        const missing = [...(photoUploaded ? [] : ['photo']), ...missingBackgrounds]
        return {
            status: 'warning',
            title: 'Caregiver Added With Missing Details',
            description: `The caregiver was added, but these couldn't be saved: ${missing.join(', ')}. You can add them from their profile.`,
        }
    }

    const submit = async () => {
        try {
            setLoading(true)
            const response = await caregiverApi.createBranchCaregiver(branchId, {
                first_name: demographicsData?.firstName,
                last_name: demographicsData?.lastName,
                initial: demographicsData?.initial,
                email: demographicsData?.email,
                gender: demographicsData?.gender,
                date_of_birth: demographicsData?.dateOfBirth ? format(new Date(demographicsData?.dateOfBirth), 'yyyy-MM-dd') : '',
                marital_status: demographicsData?.maritalStatus,
                ethnicity: demographicsData?.ethnicity,
                ssn: demographicsData?.ssn,
                branch_ids: demographicsData?.branch_ids,
                primary_branch_id: demographicsData?.primary_branch_id,
            })

            const caregiverId = response?.data?.id

            let primaryPhoneNumbers = []
            contactAndAddressData?.primaryPhoneNumbers?.map(item =>
                primaryPhoneNumbers.push({
                    type: item?.type,
                    number: item?.number,
                    is_primary: false,
                }),
            )
            await caregiverApi.createCaregiverContact(branchId, caregiverId, {
                type: 'primary',
                name: demographicsData?.firstName + ' ' + demographicsData?.lastName,
                phones: [
                    {
                        type: contactAndAddressData?.primaryPhoneNumberType,
                        number: contactAndAddressData?.primaryPhoneNumber,
                        is_primary: true,
                    },
                    ...primaryPhoneNumbers,
                ],
                addresses: [
                    {
                        type: 'home',
                        street_line1: contactAndAddressData?.streetAddressLine1,
                        street_line2: contactAndAddressData?.streetAddressLine2,
                        city: contactAndAddressData?.city,
                        state: contactAndAddressData?.state,
                        zip_code: contactAndAddressData?.zipCode,
                        is_primary: true,
                    },
                ],
            })

            await caregiverApi.createCaregiverEmploymentInfo(branchId, caregiverId, {
                employment_type: employmentInfoData?.employmentType,
                employment_status: employmentInfoData?.employmentStatus,
                application_date: employmentInfoData?.applicationDate ? format(new Date(employmentInfoData?.applicationDate), 'yyyy-MM-dd') : '',
                referral_source: employmentInfoData?.referralSource,
                referral_person: employmentInfoData?.referralPerson,
            })

            await caregiverApi.createCaregiverPreference(branchId, caregiverId, {
                languages: languageAndPreferencesData?.languages,
                comfortable_with_dogs: languageAndPreferencesData?.comfortableWithDogs,
                comfortable_with_cats: languageAndPreferencesData?.comfortableWithCats,
                assists_with_pet_care: languageAndPreferencesData?.ableToAssistWithPetCare,
                prefers_male: languageAndPreferencesData?.man,
                prefers_female: languageAndPreferencesData?.woman,
                no_gender_preference: !languageAndPreferencesData?.man && !languageAndPreferencesData?.woman,
                available_full_time: languageAndPreferencesData?.fullTime,
                available_part_time: languageAndPreferencesData?.partTime,
                available_live_in: languageAndPreferencesData?.liveIn,
                available_hourly_shifts: languageAndPreferencesData?.hourlyShifts,
                comfortable_with_smokers: languageAndPreferencesData?.comfortableWorkingInaHouseholdWithSmokers,
                has_valid_drivers_license: languageAndPreferencesData?.hasValidDriversLicenseClient,
                skilled_wound_care: languageAndPreferencesData?.skilledInWoundCareManagement,
                experienced_physical_therapy: languageAndPreferencesData?.experiencedInPhysicalTherapyAssistance,
                experienced_hoyer_lift: languageAndPreferencesData?.experiencedUsingMobilityEquipment,
            })

            if (additionalNotesData?.internalNote) {
                await caregiverApi.createCaregiverNote(branchId, caregiverId, {notes: additionalNotesData?.internalNote})
            }

            // Last, and best-effort: the caregiver is fully created by now, so a
            // failed photo or Backgrounds item must not send the user back to
            // submit again, which would create a duplicate. Anything missing can
            // be added from the profile.
            const photoUploaded = await uploadPhoto(caregiverId)
            const missingBackgrounds = await saveBackgrounds(caregiverId)
            setLoading(false)

            setMessageBox(addedMessage(photoUploaded, missingBackgrounds))
            navigate('/caregivers-management')
        } catch (error) {
            setLoading(false)
            const {title, description} = parseError(error)
            if (description) {
                setMessageBox({status: 'error', title, description})
            }
        }
    }

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <Link to="/caregivers-management">
                            <IconButton>
                                <ArrowBack />
                            </IconButton>
                        </Link>
                        <Typography variant="h6" color="text.primary">
                            Add New Caregiver
                        </Typography>
                    </div>
                    <div className="actions">
                        <Button variant="outlined" onClick={() => navigate('/caregivers-management')} style={{flex: 1}}>
                            Cancel
                        </Button>
                        {activeStep != 0 && (
                            <Button variant="outlined" onClick={previous} style={{flex: 1}}>
                                Previous
                            </Button>
                        )}
                        <Button variant="contained" onClick={next} style={{flex: 1}}>
                            {nextLabel}
                        </Button>
                    </div>
                    <br />
                </>
            ) : (
                <>
                    <Typography variant="body1" color="text.secondary">
                        <Link className="link" to="/caregivers-management">
                            Caregivers Management
                        </Link>
                        {' / '} Add New Caregiver
                    </Typography>
                    <br />
                    <div className="header">
                        <div className="back-container">
                            <Link className="back-button" to="/caregivers-management">
                                <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                            </Link>
                            <Typography variant="h5" color="text.primary">
                                Add New Caregiver
                            </Typography>
                        </div>
                        <div className="actions">
                            <Button variant="outlined" onClick={() => navigate('/caregivers-management')}>
                                Cancel
                            </Button>
                            {activeStep != 0 && (
                                <Button variant="outlined" onClick={previous}>
                                    Previous
                                </Button>
                            )}
                            <Button variant="contained" onClick={next}>
                                {nextLabel}
                            </Button>
                        </div>
                    </div>
                </>
            )}
            <br />
            <br />
            {mobile ? (
                <>
                    <Tabs value={activeStep} onChange={(e, newValue) => setActiveStep(newValue)} variant="scrollable">
                        <Tab label="Demographics" disabled />
                        <Tab label="Contact & Address" disabled />
                        <Tab label="Employee Info" disabled />
                        <Tab label="Language & Preferences" disabled />
                        <Tab label="Backgrounds" disabled />
                        <Tab label="Additional Info" disabled />
                    </Tabs>
                    <br />
                    <br />
                    <div style={{display: activeStep != 0 ? 'none' : 'block'}}>
                        <Demographics
                            form={demographicsForm}
                            avatarUrl={avatarUrl}
                            setAvatarUrl={setAvatarUrl}
                            file={file}
                            setFile={setFile}
                            branches={branches}
                            selectedBranches={selectedBranches}
                            setSelectedBranches={setSelectedBranches}
                        />
                    </div>
                    <div style={{display: activeStep != 1 ? 'none' : 'block'}}>
                        <ContactAndAddress form={contactAndAddressForm} />
                    </div>
                    <div style={{display: activeStep != 2 ? 'none' : 'block'}}>
                        <EmploymentInfo form={employmentInfoForm} />
                    </div>
                    <div style={{display: activeStep != 3 ? 'none' : 'block'}}>
                        <LanguageAndPreferences form={languageAndPreferencesForm} />
                    </div>
                    <div style={{display: activeStep != 4 ? 'none' : 'block'}}>
                        <Backgrounds form={backgroundsForm} />
                    </div>
                    <div style={{display: activeStep != 5 ? 'none' : 'block'}}>
                        <AdditionalNotes form={additionalNotesForm} />
                    </div>
                </>
            ) : (
                <div className="container">
                    <div className="step">
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {steps.map((label, index) => (
                                <Step key={label} completed={index < activeStep}>
                                    <StepLabel
                                        slots={{
                                            stepIcon: ({active, completed, icon}) =>
                                                completed ? (
                                                    <CheckCircle color="primary" style={{fontSize: 28}} />
                                                ) : (
                                                    <Box
                                                        className="step-icon"
                                                        sx={theme => ({
                                                            backgroundColor: active ? theme.palette.primary.main : theme.palette.text.disabled,
                                                        })}>
                                                        {icon}
                                                    </Box>
                                                ),
                                        }}>
                                        <Typography variant="subtitle2">{label}</Typography>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </div>
                    <div className="content">
                        {activeStep == 0 && (
                            <Demographics
                                form={demographicsForm}
                                avatarUrl={avatarUrl}
                                setAvatarUrl={setAvatarUrl}
                                file={file}
                                setFile={setFile}
                                branches={branches}
                                selectedBranches={selectedBranches}
                                setSelectedBranches={setSelectedBranches}
                            />
                        )}
                        {activeStep == 1 && <ContactAndAddress form={contactAndAddressForm} />}
                        {activeStep == 2 && <EmploymentInfo form={employmentInfoForm} />}
                        {activeStep == 3 && <LanguageAndPreferences form={languageAndPreferencesForm} />}
                        {activeStep == 4 && <Backgrounds form={backgroundsForm} />}
                        {activeStep == 5 && <AdditionalNotes form={additionalNotesForm} />}
                    </div>
                </div>
            )}
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
        </>
    )
}
